/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useNotification } from "components/Shared/Notifications/Notifications";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setStatus, AuthenticationError } from "../../dataHandling";
import MenuScheduler from "../MenuScheduler";
import MenuSelection from "../MenuSelection";
import TableScheduler from "../TableScheduler";
import {
  DEFAULT_FILTERS,
  readFiltersFromUrl,
  writeFiltersToUrl,
} from "../../utils/urlFilters";

import { getConfig } from "../../getConfig";
const { BACKEND_WS_URL } = getConfig();

const PanelScheduler = ({ idToken, getToken, onLogout }) => {
  const { notifySuccess, notifyError } = useNotification();

  // Hold everything in a single object. The filters start from the URL so a
  // refresh (or a shared link) restores the view the user was looking at.
  const [schedulerState, setSchedulerState] = useState(() => ({
    dictJobs: {},
    dictSchedulerStatuses: { ALL: "Loading" },
    dictMachineUsages: { ALL: null },
    listSchedulers: ["ALL"],
    schedulersLoaded: false,
    listJobSelected: [],
    ...DEFAULT_FILTERS,
    ...readFiltersFromUrl(),
  }));

  // Refs for the WebSocket, reconnection timer, and reconnect flag.
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnect = useRef(true);

  const getStatus_All = (dictSchedulerStatuses) => {
    let nOk = 0;
    let nErrors = 0;
    let nLoading = 0;

    Object.entries(dictSchedulerStatuses).forEach(([key, status]) => {
      if (key === "ALL") return;
      if (status === "Not Running") {
        nErrors++;
      } else if (status === "Running") {
        nOk++;
      } else if (status === "Loading") {
        nLoading++;
      }
    });

    // Determine overall status based on the counters
    if (nLoading > 0) {
      return "Loading";
    } else if (nErrors > 0 && nOk > 0) {
      return "Partial";
    } else if (nErrors > 0) {
      return "Not Running";
    } else {
      return "Running";
    }
  };

  // 4) The core function: merge fresh data from the server into `schedulerState`.
  const updateSchedulerData = useCallback((data) => {
    setSchedulerState((prev) => {
      // figure out newly seen schedulers from `data` keys
      const incomingSchedulers = Object.keys(data);
      // remove "ALL" from old list
      const prevSchedulersNoAll = prev.listSchedulers.filter(
        (name) => name !== "ALL"
      );
      // merge old + new
      let mergedSchedulers = Array.from(
        new Set([...prevSchedulersNoAll, ...incomingSchedulers])
      );

      // If we have more than one scheduler, re-insert "ALL" at the front
      if (mergedSchedulers.length > 1) {
        mergedSchedulers = ["ALL", ...mergedSchedulers];
      }

      // Copy old dictionaries
      const newDictSchedulerStatuses = { ...prev.dictSchedulerStatuses };
      const newDictMachineUsages = { ...prev.dictMachineUsages };
      const newDictJobs = { ...prev.dictJobs };

      let foundJobChanges = false;

      Object.entries(data).forEach(([schedulerName, details]) => {
        newDictSchedulerStatuses[schedulerName] = details.schedulerStatus;
        newDictMachineUsages[schedulerName] = details.machineUsage;
        if (details.jobRuns) {
          newDictJobs[schedulerName] = JSON.parse(details.jobRuns).data;
          foundJobChanges = true;
        }
      });

      // If we have multiple schedulers, ensure "ALL" placeholders exist
      if (mergedSchedulers.length > 1) {
        newDictSchedulerStatuses["ALL"] = getStatus_All(
          newDictSchedulerStatuses
        );
        newDictMachineUsages["ALL"] = null;
      }

      return {
        ...prev,
        dictSchedulerStatuses: newDictSchedulerStatuses,
        dictMachineUsages: newDictMachineUsages,
        dictJobs: foundJobChanges ? newDictJobs : prev.dictJobs,
        listSchedulers: mergedSchedulers,
        // The first payload always carries every scheduler the user can see,
        // so from here on the list can be trusted to validate the selection.
        schedulersLoaded: prev.schedulersLoaded || incomingSchedulers.length > 0,
      };
    });
  }, []);

  // Track the freshest token so reconnects never present a stale one
  const latestTokenRef = useRef(idToken);
  useEffect(() => {
    latestTokenRef.current = idToken;
  }, [idToken]);
  const authRetryCountRef = useRef(0);

  // 3) Connect/reconnect the WebSocket when idToken changes
  useEffect(() => {
    const MAX_AUTH_RETRIES = 3;
    const AUTH_RETRY_DELAYS = [0, 2000, 5000];

    const connectWebSocket = (tokenOverride = null) => {
      shouldReconnect.current = true;
      // Always use the freshest token for (re)connection. The bearer travels
      // in the Sec-WebSocket-Protocol header ("jwt", <token>) instead of the
      // URL query string, so it never lands in server access logs.
      const token = tokenOverride || latestTokenRef.current;
      const ws = new WebSocket(`${BACKEND_WS_URL}/scheduler/`, ["jwt", token]);
      wsRef.current = ws;

      ws.onopen = () => {
      };

      ws.onmessage = (event) => {
        // Data is flowing, so authentication succeeded: reset the retry budget
        authRetryCountRef.current = 0;
        const message = JSON.parse(event.data);
        updateSchedulerData(message);
      };

      ws.onerror = (error) => {
        // Disable the reconnect trigger so we don't loop infinitely.
        //shouldReconnect.current = false;
        //onLogout("WebSocket encountered an error:" + error);
      };

      // Authentication rejected: the token is usually just stale, e.g. after
      // waking from overnight sleep. Mint a fresh one and retry with backoff;
      // only log out once the retries are exhausted.
      const retryAuthentication = () => {
        if (!getToken || authRetryCountRef.current >= MAX_AUTH_RETRIES) {
          shouldReconnect.current = false;
          authRetryCountRef.current = 0;
          onLogout("Your session has expired. Please log in again.");
          return;
        }
        authRetryCountRef.current++;
        const delay =
          AUTH_RETRY_DELAYS[
            Math.min(authRetryCountRef.current - 1, AUTH_RETRY_DELAYS.length - 1)
          ];
        reconnectTimeoutRef.current = setTimeout(async () => {
          let freshToken = null;
          try {
            freshToken = await getToken(true);
          } catch {}
          if (freshToken) {
            latestTokenRef.current = freshToken;
            connectWebSocket(freshToken);
          } else {
            retryAuthentication();
          }
        }, delay);
      };

      ws.onclose = (event) => {
        // 1006 (abnormal close, e.g. network drop) falls through to the
        // generic reconnect below so the dashboard heals itself instead of
        // sitting stale until the next token refresh re-render.
        if (event.code === 4003 || event.code === 4001) {
          // 4003/4001 - Authentication error (likely expired token)
          // This happens for both Firebase and Azure when tokens expire
          retryAuthentication();
          return;
        }
        if (event.code === 4007) {
          // 4007 - User not authorized
          shouldReconnect.current = false;
          onLogout("User not authorized");
          return;
        }
        if (event.code === 4008) {
          // 4008 - No schedulers found for the user
          shouldReconnect.current = false;
          onLogout("No schedulers found for the user");
          return;
        }

        // Attempt to reconnect only if we haven't disabled it
        if (!shouldReconnect.current) {
          return;
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };
    };

    connectWebSocket();

    // Cleanup
    return () => {
      // Prevent onclose from scheduling a zombie reconnect after this effect
      // is torn down (unmount or idToken change)
      shouldReconnect.current = false;
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [idToken, getToken, onLogout, updateSchedulerData]);

  // Auth errors reported by child viewers (log streams, REST calls):
  // refresh the token once before logging out. A genuinely new token
  // re-renders the children with a fresh idToken prop, which reconnects
  // their sockets; if no new token can be minted, the session is really gone.
  const handleChildAuthError = useCallback(
    async (message) => {
      if (getToken) {
        try {
          const freshToken = await getToken(true);
          if (freshToken && freshToken !== latestTokenRef.current) {
            latestTokenRef.current = freshToken;
            return;
          }
        } catch {}
      }
      onLogout(message);
    },
    [getToken, onLogout]
  );

  // Mirror the filters back into the URL whenever any of them changes.
  useEffect(() => {
    writeFiltersToUrl({
      schedulerSelected: schedulerState.schedulerSelected,
      statusFilter: schedulerState.statusFilter,
      searchTerm: schedulerState.searchTerm,
      sortField: schedulerState.sortField,
      sortOrder: schedulerState.sortOrder,
    });
  }, [
    schedulerState.schedulerSelected,
    schedulerState.statusFilter,
    schedulerState.searchTerm,
    schedulerState.sortField,
    schedulerState.sortOrder,
  ]);

  // 5) Handling "selection"/filters/other user actions:
  const handleCancelSelection = () => {
    setSchedulerState((prev) => ({
      ...prev,
      listJobSelected: [],
    }));
  };

  const handleLogout = (logoutReason = null) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    onLogout(logoutReason);
  };

  const handleJobSelectionChange = (listJobs) => {
    setSchedulerState((prev) => ({
      ...prev,
      listJobSelected: listJobs,
    }));
  };

  const handleStatusFilterClick = (newFilterArray) => {
    setSchedulerState((prev) => ({
      ...prev,
      statusFilter: newFilterArray,
    }));
  };

  const handleSchedulerChange = (value) => {
    setSchedulerState((prev) => ({
      ...prev,
      schedulerSelected: value,
    }));
  };

  const handleSearchChange = (value) => {
    setSchedulerState((prev) => ({
      ...prev,
      searchTerm: value,
    }));
  };

  const handleSortChange = (field) => {
    setSchedulerState((prev) => {
      // If user is clicking the same field, flip asc/desc. Otherwise reset to asc.
      if (prev.sortField === field) {
        return {
          ...prev,
          sortOrder: prev.sortOrder === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          ...prev,
          sortField: field,
          sortOrder: "asc",
        };
      }
    });
  };

  // 6) Generic requests to change job status
  const handleJobStatusChangeRequest = async (
    job,
    newStatus,
    newCommandLine
  ) => {
    await sendRequestStatusChange([job], newStatus, newCommandLine);
  };

  const handleSetRun = () => {
    requestStatusChange("READY");
  };

  const handleSetForceStart = () => {
    requestStatusChange("FORCE_START_CHILDREN_WAITING");
  };

  const handleSetOk = () => {
    requestStatusChange("OK");
  };

  const handleSetWarning = () => {
    requestStatusChange("WARN");
  };

  const handleSetError = () => {
    requestStatusChange("ERROR");
  };

  const requestStatusChange = async (newStatus) => {
    await sendRequestStatusChange(schedulerState.listJobSelected, newStatus);
    handleCancelSelection();
  };

  const sendRequestStatusChange = async (
    listJobs,
    newStatus,
    newCommandLine = ""
  ) => {
    try {
      const listRequests = listJobs.map((job) => ({
        schedulerName: job.SchedulerName,
        jobName: job.Name,
        oldStatus: job.Status,
        newStatus: newStatus,
        newCommandLine: newCommandLine,
      }));

      const response = await setStatus(listRequests, idToken, getToken);
      // For safety, parse the JSON
      const listResponses = JSON.parse(response);

      listResponses.forEach((item) => {
        if (item.completed) {
          notifySuccess(`${item.jobName} status changed to "${item.status}".`);
        } else {
          notifyError(`${item.jobName} status has not changed.`);
        }
      });
    } catch (error) {
      if (error instanceof AuthenticationError) {
        // Refresh-and-retry before logging out, same as the other auth paths
        handleChildAuthError(error.message);
      } else {
        toast.error("Unexpected response format: data list not found");
      }
    }
  };

  // 7) Render
  const {
    dictJobs,
    dictSchedulerStatuses,
    dictMachineUsages,
    listSchedulers,
    schedulersLoaded,
    statusFilter,
    searchTerm,
    sortField,
    sortOrder,
    schedulerSelected,
    listJobSelected,
  } = schedulerState;

  return (
    <div
      className="panel-scheduler-container"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {listJobSelected.length === 0 ? (
        <MenuScheduler
          schedulerSelected={schedulerSelected}
          listSchedulers={listSchedulers}
          schedulersLoaded={schedulersLoaded}
          dictJobs={dictJobs}
          dictSchedulerStatuses={dictSchedulerStatuses}
          dictMachineUsages={dictMachineUsages}
          statusFilter={statusFilter}
          onStatusFilterClick={handleStatusFilterClick}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onSchedulerChange={handleSchedulerChange}
          onLogout={handleLogout}
        />
      ) : (
        <MenuSelection
          onCancelSelection={handleCancelSelection}
          listJobSelected={listJobSelected}
          onSetRun={handleSetRun}
          onSetForceStart={handleSetForceStart}
          onSetOk={handleSetOk}
          onSetWarning={handleSetWarning}
          onSetError={handleSetError}
        />
      )}

      <TableScheduler
        schedulerSelected={schedulerSelected}
        dictJobs={dictJobs}
        statusFilter={statusFilter}
        searchTerm={searchTerm}
        sortField={sortField}
        sortOrder={sortOrder}
        listJobSelected={listJobSelected}
        onSortChange={handleSortChange}
        onJobSelectionChange={handleJobSelectionChange}
        onStatusChangeRequest={handleJobStatusChangeRequest}
        onAuthError={handleChildAuthError}
        idToken={idToken}
        getToken={getToken}
      />
    </div>
  );
};

export default PanelScheduler;
